import React, { useCallback, useEffect, useState } from "react"
import { Badge, Button, Card, CardBody, Col, Input, Label, Row, Spinner } from "reactstrap"
import { getAppConstant, updateAppConstant } from "helpers/backendHelpers/appConstant"

const WebVersionUpdateCard = () => {
  const [currentSavedTime, setCurrentSavedTime] = useState(null)
  const [timestampInput, setTimestampInput] = useState("")
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const res = await getAppConstant()
      const time = res?.payload?.latestWebUpdatedTime
      if (time) {
        const numTime = Number(time)
        setCurrentSavedTime(numTime)
        setTimestampInput(String(numTime))
      }
    } catch (err) {
      console.error("Failed to fetch app constant:", err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSetCurrentTimestamp = () => {
    const now = Date.now()
    setTimestampInput(String(now))
  }

  const handleUpdate = async () => {
    if (!timestampInput || isNaN(Number(timestampInput))) {
      return
    }

    try {
      setUpdating(true)
      const numericVal = Number(timestampInput)
      await updateAppConstant({
        constantData: {
          latestWebUpdatedTime: numericVal,
        },
      })
      setCurrentSavedTime(numericVal)
    } catch (err) {
      console.error("Failed to update app constant:", err)
    } finally {
      setUpdating(false)
    }
  }

  const formatReadableDate = ms => {
    if (!ms) return "-"
    try {
      return new Date(ms).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
    } catch (_) {
      return String(ms)
    }
  }

  const isValidInput = timestampInput && !isNaN(Number(timestampInput))

  return (
    <Card className="mb-3">
      <CardBody>
        {loading ? (
          <div className="d-flex align-items-center py-1">
            <Spinner size="sm" color="primary" className="me-2" />
            <span className="text-muted">Loading settings...</span>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
              <div>
                <h5 className="font-size-16 fw-bold mb-1">Web App Version Update</h5>
                <p className="text-muted font-size-13 mb-0">
                  Saved as <code>latestWebUpdatedTime</code>. Update the timestamp to publish a new web version.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="text-muted font-size-13">Current saved</span>
                <Badge color="primary" className="font-size-13 px-2 py-1">
                  {currentSavedTime || "Not Set"}
                </Badge>
                {currentSavedTime && (
                  <span className="text-muted font-size-13">({formatReadableDate(currentSavedTime)})</span>
                )}
              </div>
            </div>

            <Row className="align-items-end g-3">
              <Col md={5} lg={4}>
                <Label className="form-label">Timestamp (ms)</Label>
                <Input
                  type="text"
                  placeholder="Timestamp in ms"
                  value={timestampInput}
                  onChange={e => setTimestampInput(e.target.value.trim())}
                />
              </Col>
              <Col xs="auto">
                <Button
                  color="info"
                  outline
                  type="button"
                  onClick={handleSetCurrentTimestamp}
                  title="Fill current Date.now() timestamp"
                >
                  <i className="mdi mdi-clock-outline me-1"></i>
                  Set Current Timestamp
                </Button>
              </Col>
              <Col xs="auto">
                <Button color="primary" type="button" disabled={updating || !timestampInput} onClick={handleUpdate}>
                  {updating ? <Spinner size="sm" /> : "Update"}
                </Button>
              </Col>
            </Row>

            {isValidInput && (
              <div className="border-top mt-3 pt-3 text-muted font-size-13">
                <i className="mdi mdi-eye-outline me-1 text-primary"></i>
                Preview: <span className="fw-semibold text-dark">{formatReadableDate(Number(timestampInput))}</span>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}

export default WebVersionUpdateCard
